import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core'
import { Router } from '@angular/router'
import {
  ApiService,
  Appointment,
  AppointmentModel,
  eligibilityDisplayStatus,
  eligibilityStatusColor,
  eligibilityStatusIcon,
  getEligibilityStatusText, intakeStatusColor, intakeStatusText,
  isSelfPayLikeInsurance,
  Patient,
  PatientDataService,
  PatientEligibilityStatus,
  PatientInsurance,
  PatientInsuranceStatus,
  PatientInsuranceType,
} from '@mmx/shared'
import { TranslateService } from '@ngx-translate/core'
import { cloneDeep } from 'lodash'
import { BehaviorSubject, Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

/** The `eligibilityDetails` keys a user can mark by hand. */
type MarkableInsurance = 'primary' | 'secondary'

@Component({
  selector: 'mmx-eligibility-status-icon',
  templateUrl: 'eligibility-status-icon.component.html',
  styleUrls: ['eligibility-status-icon.component.scss'],
  standalone: false,
})
export class EligibilityStatusIconComponent implements OnInit, OnChanges, OnDestroy {
  @Input() patient?: Patient
  @Input() appointment?: AppointmentModel
  @Input() status?: Appointment.ELIGIBILITY_STATUS
  @Input() workflow = false

  public inProgressFor: MarkableInsurance | null = null
  public tooltipText$ = new BehaviorSubject<string>('')
  public ELIGIBILITY_STATUS = Appointment.ELIGIBILITY_STATUS
  public PATIENT_INSURANCE_TYPE = PatientInsuranceType
  public eligibilityDisplayStatus = eligibilityDisplayStatus
  public eligibilityStatusColor = eligibilityStatusColor
  public eligibilityStatusIcon = eligibilityStatusIcon
  public getEligibilityStatusText = getEligibilityStatusText

  /** The patient's policies with their live carrier lookup; only loaded on patient pages. */
  private patientInsurances: PatientInsurance[] = []
  private onDestroy$ = new Subject<void>()

  public constructor(
    private router: Router,
    private api: ApiService,
    private translateService: TranslateService,
    private patientDataService: PatientDataService,
  ) {}

  ngOnInit() {
    this.updateTooltip()
    this.patientDataService.insurances$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((insurances) => (this.patientInsurances = insurances))
  }

  ngOnDestroy() {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.appointment && !changes.appointment.firstChange) {
      this.updateTooltip()
    }
  }

  goto(...commands: string[]) {
    this.router.navigate(commands)
  }

  get inProgress(): boolean {
    return this.inProgressFor !== null
  }

  /** The Workflow shields patch independently, so only the one being saved should spin. */
  isMarking(insuranceType: PatientInsuranceType): boolean {
    return this.inProgressFor === this.markableKeyFor(insuranceType)
  }

  isActive(status: Appointment.ELIGIBILITY_STATUS): boolean {
    return [
      Appointment.ELIGIBILITY_STATUS.ACTIVE,
      Appointment.ELIGIBILITY_STATUS.NOT_REQUIRED,
    ].includes(status)
  }

  markInsuranceAs(
    type: MarkableInsurance,
    updateStatus: 'active' | 'inactive',
  ): void {
    if (this.inProgress) {
      return
    }

    this.setManualVerification(
      type,
      updateStatus === 'active'
        ? PatientEligibilityStatus.ACTIVE
        : PatientEligibilityStatus.INACTIVE,
    )
  }

  private findMarkableInsurance(type: MarkableInsurance) {
    const wanted =
      type === 'secondary'
        ? PatientInsuranceType.SECONDARY
        : PatientInsuranceType.PRIMARY
    const ofType =
      this.appointment?.insurances?.filter((i) => i.type === wanted) ?? []

    return (
      ofType.find((i) => i.status === PatientInsuranceStatus.Active) ??
      ofType.find((i) => i.status === PatientInsuranceStatus.NotVerified) ??
      ofType[0]
    )
  }

  /**
   * Persist the mark on the policy rather than this appointment, so the API fans the status
   * out to every appointment the coverage applies to.
   */
  private setManualVerification(
    type: MarkableInsurance,
    status: PatientEligibilityStatus.ACTIVE | PatientEligibilityStatus.INACTIVE,
  ): void {
    const insurance = this.findMarkableInsurance(type)

    if (!insurance?.id) {
      return
    }

    this.inProgressFor = type
    this.api
      .put<{ manuallyVerified?: boolean, eligibilityStatus?: PatientEligibilityStatus }>(
        `/insurances/policies/${insurance.id}?appointmentId=${this.appointment.id}`,
        {
          patientId: this.appointment.patientId,
          manuallyVerified: true,
          manualEligibilityStatus: status,
        },
      )
      .subscribe({
        next: (response) => {
          const saved = response?.data ?? {}

          if (saved.eligibilityStatus != null) {
            insurance.manuallyVerified = saved.manuallyVerified === true
            insurance.eligibilityStatus = saved.eligibilityStatus
            this.setEligibilityDetail(
              type,
              saved.eligibilityStatus as string as Appointment.ELIGIBILITY_STATUS,
            )
          }

          this.updateTooltip()
          this.inProgressFor = null
        },
        error: () => {
          this.inProgressFor = null
        },
      })
  }

  private setEligibilityDetail(
    type: MarkableInsurance,
    status: Appointment.ELIGIBILITY_STATUS,
  ): void {
    const eligibilityDetails = cloneDeep(this.appointment.eligibilityDetails)
    eligibilityDetails[type] = { id: '', status }
    this.appointment.eligibilityDetails = eligibilityDetails
  }

  /**
   * Get the text for the eligibility shield icon in Workflow
   * Reports a manual mark in the insurance-status wording Workflow already uses, so "Set as
   * Active" never surfaces as "Not Required"; every other state keeps reading the insurance.
   */
  getEligibilityShieldText(insuranceType: PatientInsuranceType): string {
    // Match the insurances card: a self-pay-like carrier reads "Not Required", not a verification status.
    if (isSelfPayLikeInsurance(this.findInsurance(insuranceType))) {
      return 'common.not-required'
    }

    const status = this.eligibilityStatusFor(insuranceType)

    if (this.isActive(status)) {
      return 'common.active'
    }

    if (status === Appointment.ELIGIBILITY_STATUS.INACTIVE) {
      return 'common.inactive'
    }

    return this.insuranceStatusText(insuranceType)
  }

  /** Marking writes a status onto a coverage, so it needs one on file that expects verification. */
  canMarkInsurance(insuranceType: PatientInsuranceType): boolean {
    const insurance = this.findInsurance(insuranceType)

    return insurance != null && !isSelfPayLikeInsurance(insurance)
  }

  canMarkManually(insuranceType: PatientInsuranceType): boolean {
    const insurance = this.findMarkableInsurance(this.markableKeyFor(insuranceType))
    const lookup = this.patientInsurances.find((policy) => policy.id === insurance?.id)?.lookup

    if (lookup) {
      return lookup.noConnectivity === true
    }

    return insurance?.manuallyVerified === true || insurance?.eligibilityStatus === PatientEligibilityStatus.NO_CONNECTION
  }

  /**
   * Hover text for the Workflow shields, matching the standalone tooltip so a hard fail
   * ("will not attempt again") stays distinguishable from a soft fail ("will try again later").
   */
  getEligibilityShieldTooltip(insuranceType: PatientInsuranceType): string {
    if (isSelfPayLikeInsurance(this.findInsurance(insuranceType))) {
      return getEligibilityStatusText(Appointment.ELIGIBILITY_STATUS.NOT_REQUIRED)
    }

    const status = this.eligibilityStatusFor(insuranceType)

    // A manual mark persists NOT_REQUIRED, whose text would contradict the "Active" label.
    return getEligibilityStatusText(
      this.isActive(status) ? Appointment.ELIGIBILITY_STATUS.ACTIVE : status,
    )
  }

  /** Keyed off coverage rather than eligibility so a never-pulled secondary can still be marked. */
  showSecondaryShield(): boolean {
    return this.findInsurance(PatientInsuranceType.SECONDARY) != null
      || this.eligibilityStatusFor(PatientInsuranceType.SECONDARY) !== Appointment.ELIGIBILITY_STATUS.NONE
  }

  private insuranceStatusText(insuranceType: PatientInsuranceType): string {
    switch (this.findInsurance(insuranceType)?.status) {
      case PatientInsuranceStatus.Active:
        return 'common.active'
      case PatientInsuranceStatus.Inactive:
        return 'common.inactive'
      case PatientInsuranceStatus.NotVerified:
        return 'common.not-verified'
      default:
        return 'common.unknown'
    }
  }

  private findInsurance(insuranceType: PatientInsuranceType): PatientInsurance | undefined {
    return this.appointment?.insurances?.find((insurance) => insurance.type === insuranceType)
  }

  private eligibilityStatusFor(insuranceType: PatientInsuranceType): Appointment.ELIGIBILITY_STATUS {
    const status = insuranceType === PatientInsuranceType.SECONDARY
      ? this.appointment?.eligibilitySecondaryStatus
      : this.appointment?.eligibilityPrimaryStatus

    return status ?? Appointment.ELIGIBILITY_STATUS.NONE
  }

  private markableKeyFor(insuranceType: PatientInsuranceType): MarkableInsurance {
    return insuranceType === PatientInsuranceType.SECONDARY ? 'secondary' : 'primary'
  }

  private updateTooltip(): void {
    this.tooltipText$.next(
      this.translateService.instant('common.primary') +
        ': ' +
        this.translateService.instant(
          getEligibilityStatusText(this.appointment?.eligibilityPrimaryStatus),
        ) +
        (this.appointment?.eligibilitySecondaryStatus
          ? '\n' +
            this.translateService.instant('common.secondary') +
            ': ' +
            this.translateService.instant(
              getEligibilityStatusText(
                this.appointment?.eligibilitySecondaryStatus,
              ),
            )
          : ''),
    )
  }

  protected readonly intakeStatusColor = intakeStatusColor
  protected readonly intakeStatusText = intakeStatusText
}
